// The expression language quantities are written in. A small, safe grammar with its own
// parser and evaluator — never `eval`.
//
//   numbers        24   0.125   .5
//   text           'Door'   "Type A"
//   names          width   nose_height   reveal_left
//   arithmetic     + - * /   ( )
//   comparisons    =  ==  !=  <>  <  <=  >  >=
//   logic          and  or  not
//   functions      if(cond, a, b)  roundup(x)  rounddown(x)  round(x)  ceiling(x, step)
//                  floor(x, step)  min(a, b, …)  max(a, b, …)  abs(x)  blank(x)
//   across a group sum(expr)  count()  count_if(cond)  count_if(key, cond)
//   across the pool pool_sum(expr)  pool_count()  pool_count_if(cond): every item on the job
//                  (the pool), whatever kind of item; one that has no such name is skipped
//
// Blank behaves as it does in the spreadsheet: zero in arithmetic, empty in text.

import { Rat } from './rational.ts';

export type Value = Rat | string | boolean | null;

export class ExprError extends Error {}

// ---------- tokens ----------

type Tok =
  | { t: 'num'; v: Rat; pos: number }
  | { t: 'str'; v: string; pos: number }
  | { t: 'id'; v: string; pos: number }
  | { t: 'op'; v: string; pos: number }
  | { t: 'end'; pos: number };

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/[0-9.]/.test(c)) {
      const m = /^(\d+\.?\d*|\.\d+)/.exec(src.slice(i));
      if (!m) throw new ExprError(`unexpected "." at ${i}`);
      out.push({ t: 'num', v: Rat.parse(m[0]), pos: i });
      i += m[0].length;
      continue;
    }
    if (c === '"' || c === "'") {
      const end = src.indexOf(c, i + 1);
      if (end < 0) throw new ExprError(`text starting at ${i} is never closed`);
      out.push({ t: 'str', v: src.slice(i + 1, end), pos: i });
      i = end + 1;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i))!;
      out.push({ t: 'id', v: m[0], pos: i });
      i += m[0].length;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (['==', '!=', '<>', '<=', '>='].includes(two)) {
      out.push({ t: 'op', v: two, pos: i });
      i += 2;
      continue;
    }
    if ('+-*/()<>=,'.includes(c)) {
      out.push({ t: 'op', v: c, pos: i });
      i++;
      continue;
    }
    throw new ExprError(`unexpected "${c}" at ${i}`);
  }
  out.push({ t: 'end', pos: src.length });
  return out;
}

// ---------- tree ----------

export type Node =
  | { k: 'num'; v: Rat }
  | { k: 'str'; v: string }
  | { k: 'bool'; v: boolean }
  | { k: 'id'; name: string }
  | { k: 'neg'; a: Node }
  | { k: 'not'; a: Node }
  | { k: 'bin'; op: string; a: Node; b: Node }
  | { k: 'call'; fn: string; args: Node[]; src: string };

const AGGREGATES = new Set(['sum', 'count', 'count_if']);
const POOL_AGGREGATES = new Set(['pool_sum', 'pool_count', 'pool_count_if']);
const FUNCTIONS = new Set([
  'if', 'roundup', 'rounddown', 'round', 'ceiling', 'floor', 'min', 'max', 'abs', 'blank',
  ...AGGREGATES,
  ...POOL_AGGREGATES,
]);

export function parse(src: string): Node {
  const toks = tokenize(src);
  let p = 0;
  const peek = () => toks[p]!;
  const next = () => toks[p++]!;
  const isOp = (v: string) => peek().t === 'op' && (peek() as { v: string }).v === v;
  const isWord = (v: string) => peek().t === 'id' && (peek() as { v: string }).v.toLowerCase() === v;
  const expect = (v: string) => {
    if (!isOp(v)) throw new ExprError(`expected "${v}" at ${peek().pos}`);
    next();
  };

  function orExpr(): Node {
    let a = andExpr();
    while (isWord('or')) {
      next();
      a = { k: 'bin', op: 'or', a, b: andExpr() };
    }
    return a;
  }
  function andExpr(): Node {
    let a = notExpr();
    while (isWord('and')) {
      next();
      a = { k: 'bin', op: 'and', a, b: notExpr() };
    }
    return a;
  }
  function notExpr(): Node {
    if (isWord('not')) {
      next();
      return { k: 'not', a: notExpr() };
    }
    return cmpExpr();
  }
  function cmpExpr(): Node {
    const a = addExpr();
    const t = peek();
    if (t.t === 'op' && ['=', '==', '!=', '<>', '<', '<=', '>', '>='].includes(t.v)) {
      next();
      const op = t.v === '==' ? '=' : t.v === '<>' ? '!=' : t.v;
      return { k: 'bin', op, a, b: addExpr() };
    }
    return a;
  }
  function addExpr(): Node {
    let a = mulExpr();
    while (isOp('+') || isOp('-')) {
      const op = (next() as { v: string }).v;
      a = { k: 'bin', op, a, b: mulExpr() };
    }
    return a;
  }
  function mulExpr(): Node {
    let a = unary();
    while (isOp('*') || isOp('/')) {
      const op = (next() as { v: string }).v;
      a = { k: 'bin', op, a, b: unary() };
    }
    return a;
  }
  function unary(): Node {
    if (isOp('-')) {
      next();
      return { k: 'neg', a: unary() };
    }
    if (isOp('+')) {
      next();
      return unary();
    }
    return primary();
  }
  function primary(): Node {
    const t = next();
    if (t.t === 'num') return { k: 'num', v: t.v };
    if (t.t === 'str') return { k: 'str', v: t.v };
    if (t.t === 'op' && t.v === '(') {
      const e = orExpr();
      expect(')');
      return e;
    }
    if (t.t === 'id') {
      const lower = t.v.toLowerCase();
      if (lower === 'true') return { k: 'bool', v: true };
      if (lower === 'false') return { k: 'bool', v: false };
      if (isOp('(')) {
        if (!FUNCTIONS.has(lower)) throw new ExprError(`unknown function "${t.v}"`);
        next();
        const args: Node[] = [];
        if (!isOp(')')) {
          args.push(orExpr());
          while (isOp(',')) {
            next();
            args.push(orExpr());
          }
        }
        expect(')');
        return { k: 'call', fn: lower, args, src: src.slice(t.pos, peek().pos).trim() };
      }
      return { k: 'id', name: t.v };
    }
    throw new ExprError(t.t === 'end' ? 'the expression ends early' : `unexpected "${(t as { v: string }).v}" at ${t.pos}`);
  }

  const tree = orExpr();
  if (peek().t !== 'end') throw new ExprError(`unexpected text at ${peek().pos}`);
  return tree;
}

const cache = new Map<string, Node>();
export function parseCached(src: string): Node {
  let n = cache.get(src);
  if (!n) {
    n = parse(src);
    cache.set(src, n);
  }
  return n;
}

/** Every name an expression reads. Used to show an expression's inputs before any job exists. */
export function identifiers(src: string): string[] {
  const seen = new Set<string>();
  const walk = (n: Node) => {
    if (n.k === 'id') seen.add(n.name);
    else if (n.k === 'neg' || n.k === 'not') walk(n.a);
    else if (n.k === 'bin') {
      walk(n.a);
      walk(n.b);
    } else if (n.k === 'call') n.args.forEach(walk);
  };
  walk(parse(src));
  return [...seen];
}

// ---------- evaluation ----------

/**
 * Where names come from. `members` are the items of the group, for sum / count / count_if;
 * `pool` is every item in the pool (the job), for pool_sum / pool_count / pool_count_if.
 */
export interface Scope {
  get(name: string): Value | undefined;
  members?(): Scope[];
  pool?(): Scope[];
}

/** True for the one error that means "this item has no such name", which a pool aggregate skips over. */
export function isUnknownName(e: unknown): boolean {
  return e instanceof ExprError && / is not a known input, derived value or standard$/.test(e.message);
}

export interface TraceEntry {
  name: string;
  value: string;
}

export function show(v: Value): string {
  if (v === null) return '(blank)';
  if (v instanceof Rat) return v.toDecimal(6);
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return v;
}

export function toRat(v: Value): Rat {
  if (v instanceof Rat) return v;
  if (v === null || v === '') return Rat.ZERO;
  if (typeof v === 'boolean') return v ? Rat.ONE : Rat.ZERO;
  const r = Rat.tryParse(v);
  if (!r) throw new ExprError(`"${v}" is not a number`);
  return r;
}

export function truthy(v: Value): boolean {
  if (v === null) return false;
  if (typeof v === 'boolean') return v;
  if (v instanceof Rat) return !v.isZero();
  return v.trim() !== '' && v.trim().toLowerCase() !== 'false';
}

export function isBlank(v: Value): boolean {
  return v === null || (typeof v === 'string' && v.trim() === '');
}

function compare(a: Value, b: Value): number {
  const text = (v: Value) => typeof v === 'string' && Rat.tryParse(v) === null;
  if (text(a) || text(b)) {
    const sa = a === null ? '' : show(a).trim().toLowerCase();
    const sb = b === null ? '' : show(b).trim().toLowerCase();
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  }
  return toRat(a).cmp(toRat(b));
}

function ceilingTo(x: Rat, step: Rat): Rat {
  if (step.isZero()) return x;
  return Rat.of(x.div(step).ceil()).mul(step);
}
function floorTo(x: Rat, step: Rat): Rat {
  if (step.isZero()) return x;
  return Rat.of(x.div(step).floor()).mul(step);
}
function pow10(places: Value): Rat {
  const p = Number(toRat(places).floor());
  return p >= 0 ? Rat.of(10n ** BigInt(p)) : Rat.of(1n, 10n ** BigInt(-p));
}

export function evaluate(node: Node, scope: Scope, trace?: TraceEntry[]): Value {
  const note = (name: string, value: Value) => {
    if (trace && !trace.some((t) => t.name === name)) trace.push({ name, value: show(value) });
  };
  const ev = (n: Node, s: Scope, record: boolean): Value => {
    switch (n.k) {
      case 'num':
      case 'str':
      case 'bool':
        return n.v;
      case 'id': {
        const v = s.get(n.name);
        if (v === undefined) throw new ExprError(`"${n.name}" is not a known input, derived value or standard`);
        if (record) note(n.name, v);
        return v;
      }
      case 'neg':
        return toRat(ev(n.a, s, record)).neg();
      case 'not':
        return !truthy(ev(n.a, s, record));
      case 'bin': {
        if (n.op === 'and') return truthy(ev(n.a, s, record)) && truthy(ev(n.b, s, record));
        if (n.op === 'or') return truthy(ev(n.a, s, record)) || truthy(ev(n.b, s, record));
        const a = ev(n.a, s, record);
        const b = ev(n.b, s, record);
        switch (n.op) {
          case '+':
            return toRat(a).add(toRat(b));
          case '-':
            return toRat(a).sub(toRat(b));
          case '*':
            return toRat(a).mul(toRat(b));
          case '/': {
            const d = toRat(b);
            if (d.isZero()) throw new ExprError('division by zero');
            return toRat(a).div(d);
          }
          case '=':
            return compare(a, b) === 0;
          case '!=':
            return compare(a, b) !== 0;
          case '<':
            return compare(a, b) < 0;
          case '<=':
            return compare(a, b) <= 0;
          case '>':
            return compare(a, b) > 0;
          case '>=':
            return compare(a, b) >= 0;
        }
        throw new ExprError(`unknown operator ${n.op}`);
      }
      case 'call': {
        const arg = (i: number) => {
          const a = n.args[i];
          if (!a) throw new ExprError(`${n.fn}() is missing an argument`);
          return ev(a, s, record);
        };
        if (AGGREGATES.has(n.fn)) {
          const members = s.members?.();
          if (!members) throw new ExprError(`${n.fn}() needs a group of items`);
          let result: Value;
          if (n.fn === 'count') {
            result = Rat.of(BigInt(members.length));
          } else if (n.fn === 'sum') {
            const a = n.args[0];
            if (!a) throw new ExprError('sum() needs what to add up');
            result = members.reduce((acc, m) => acc.add(toRat(ev(a, m, false))), Rat.ZERO);
          } else {
            const cond = n.args.length > 1 ? n.args[1]! : n.args[0];
            const key = n.args.length > 1 ? n.args[0]! : undefined;
            if (!cond) throw new ExprError('count_if() needs a condition');
            let c = 0n;
            for (const m of members) {
              if (key && isBlank(ev(key, m, false))) continue;
              if (truthy(ev(cond, m, false))) c++;
            }
            result = Rat.of(c);
          }
          if (record) note(n.src, result);
          return result;
        }
        if (POOL_AGGREGATES.has(n.fn)) {
          const pool = s.pool?.();
          if (!pool) throw new ExprError(`${n.fn}() needs the job's items`);
          // an item that has no such name contributes nothing; any other problem is reported
          const tryOn = (expr: Node, m: Scope): Value | undefined => {
            try {
              return ev(expr, m, false);
            } catch (e) {
              if (isUnknownName(e)) return undefined;
              throw e;
            }
          };
          let result: Value;
          if (n.fn === 'pool_count') {
            result = Rat.of(BigInt(pool.length));
          } else if (n.fn === 'pool_sum') {
            const a = n.args[0];
            if (!a) throw new ExprError('pool_sum() needs what to add up');
            result = pool.reduce((acc, m) => {
              const v = tryOn(a, m);
              return v === undefined ? acc : acc.add(toRat(v));
            }, Rat.ZERO);
          } else {
            const cond = n.args[0];
            if (!cond) throw new ExprError('pool_count_if() needs a condition');
            let c = 0n;
            for (const m of pool) {
              const v = tryOn(cond, m);
              if (v !== undefined && truthy(v)) c++;
            }
            result = Rat.of(c);
          }
          if (record) note(n.src, result);
          return result;
        }
        switch (n.fn) {
          case 'if':
            return truthy(arg(0)) ? arg(1) : n.args[2] ? arg(2) : Rat.ZERO;
          case 'roundup': {
            const x = toRat(arg(0));
            const scale = n.args[1] ? pow10(arg(1)) : Rat.ONE;
            const up = Rat.of(x.abs().mul(scale).ceil()).div(scale);
            return x.sign() < 0 ? up.neg() : up;
          }
          case 'rounddown': {
            const x = toRat(arg(0));
            const scale = n.args[1] ? pow10(arg(1)) : Rat.ONE;
            const down = Rat.of(x.abs().mul(scale).floor()).div(scale);
            return x.sign() < 0 ? down.neg() : down;
          }
          case 'round': {
            const x = toRat(arg(0));
            const scale = n.args[1] ? pow10(arg(1)) : Rat.ONE;
            return Rat.of(x.mul(scale).round()).div(scale);
          }
          case 'ceiling':
            return ceilingTo(toRat(arg(0)), n.args[1] ? toRat(arg(1)) : Rat.ONE);
          case 'floor':
            return floorTo(toRat(arg(0)), n.args[1] ? toRat(arg(1)) : Rat.ONE);
          case 'min':
          case 'max': {
            if (n.args.length === 0) throw new ExprError(`${n.fn}() needs at least one value`);
            let best = toRat(arg(0));
            for (let i = 1; i < n.args.length; i++) {
              const v = toRat(arg(i));
              if (n.fn === 'min' ? v.cmp(best) < 0 : v.cmp(best) > 0) best = v;
            }
            return best;
          }
          case 'abs':
            return toRat(arg(0)).abs();
          case 'blank':
            return isBlank(arg(0));
        }
        throw new ExprError(`unknown function ${n.fn}`);
      }
    }
  };
  return ev(node, scope, true);
}

/** Parse and evaluate in one call. */
export function calc(src: string, scope: Scope, trace?: TraceEntry[]): Value {
  return evaluate(parseCached(src), scope, trace);
}

/** A scope over a plain object, for tests and one-off sums. */
export function objectScope(values: Record<string, Value | number>, members?: Record<string, Value | number>[]): Scope {
  const wrap = (o: Record<string, Value | number>): Scope => ({
    get: (name) => {
      if (!(name in o)) return undefined;
      const v = o[name]!;
      return typeof v === 'number' ? Rat.parse(v) : v;
    },
    members: members ? () => members.map((m) => wrap(m)) : undefined,
  });
  return wrap(values);
}
